import React from 'react';

const PrivacyPolicy = () => (
  <div className="container mx-auto px-4 py-8">
    <h1 className="text-3xl font-bold mb-6">Privacy Policy</h1>
    <p className="mb-4"><strong>Last updated: 2 June 2025</strong></p>

    <h2 className="text-2xl font-semibold mt-6 mb-3">1. Who We Are</h2>
    <p className="mb-4">
      QuantumSafeAudit.com – Sole Proprietorship ("we", "our", "us") is registered in The Netherlands (KvK 62781839) and located in <strong>The Hague</strong>. Our full statutory address and telephone number are available on request from the Dutch Chamber of Commerce (KvK).
    </p>
    <p className="mb-4">
      <strong>Privacy contact:</strong> <a href="mailto:privacy@quantumsafeaudit.com" className="text-blue-600 hover:underline">privacy@quantumsafeaudit.com</a>
    </p>

    <h2 className="text-2xl font-semibold mt-6 mb-3">2. Scope of This Policy</h2>
    <p className="mb-4">
      This policy applies to the web‑application that analyses the cryptographic configuration of public websites ("Service") and to any e‑mail correspondence initiated through the "Contact" button.
    </p>

    <h2 className="text-2xl font-semibold mt-6 mb-3">3. Personal Data We Process</h2>
    <div className="overflow-x-auto mb-4">
      <table className="min-w-full bg-white border border-gray-300">
        <thead>
          <tr>
            <th className="border border-gray-300 px-4 py-2">Category</th>
            <th className="border border-gray-300 px-4 py-2">Examples</th>
            <th className="border border-gray-300 px-4 py-2">Source</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="border border-gray-300 px-4 py-2"><strong>Technical data</strong></td>
            <td className="border border-gray-300 px-4 py-2">IP address (truncated for analytics), HTTP headers (incl. user‑agent), date/time of requests, scan results that may refer to a domain held by an individual</td>
            <td className="border border-gray-300 px-4 py-2">Automatically collected when you use the Service</td>
          </tr>
          <tr>
            <td className="border border-gray-300 px-4 py-2"><strong>Usage statistics (aggregated)</strong></td>
            <td className="border border-gray-300 px-4 py-2">Page views, time spent, events (e.g. button clicks), approximate geolocation (country/city), referring URLs</td>
            <td className="border border-gray-300 px-4 py-2">Automatically collected through Google Analytics (IP anonymized)</td>
          </tr>
          <tr>
            <td className="border border-gray-300 px-4 py-2"><strong>Communication data</strong></td>
            <td className="border border-gray-300 px-4 py-2">Contents of e‑mails you send us, related metadata</td>
            <td className="border border-gray-300 px-4 py-2">Provided by you when you use the Contact button</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div className="bg-gray-100 p-4 rounded-md mb-4">
      <p>We do <strong>not</strong> require account registration and therefore do not collect names, passwords or similar account credentials.</p>
    </div>

    <h2 className="text-2xl font-semibold mt-6 mb-3">4. Purposes and Legal Bases</h2>
    <div className="overflow-x-auto mb-4">
      <table className="min-w-full bg-white border border-gray-300">
        <thead>
          <tr>
            <th className="border border-gray-300 px-4 py-2">Purpose</th>
            <th className="border border-gray-300 px-4 py-2">Legal basis (Art. 6 GDPR)</th>
            <th className="border border-gray-300 px-4 py-2">Explanation</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="border border-gray-300 px-4 py-2">Provide the Service, generate scan reports, maintain logs for troubleshooting</td>
            <td className="border border-gray-300 px-4 py-2"><strong>Contract</strong> (Art. 6 (1)(b))</td>
            <td className="border border-gray-300 px-4 py-2">Processing is necessary to deliver the functionality you request.</td>
          </tr>
          <tr>
            <td className="border border-gray-300 px-4 py-2">Analyse aggregated usage of the website in order to improve content, performance and security (Google Analytics)</td>
            <td className="border border-gray-300 px-4 py-2"><strong>Consent</strong> (Art. 6 (1)(a))</td>
            <td className="border border-gray-300 px-4 py-2">Analytics cookies are set only after you give consent via the cookie banner; data are anonymised before storage.</td>
          </tr>
          <tr>
            <td className="border border-gray-300 px-4 py-2">Handle enquiries and orders sent by e‑mail; retain fiscally relevant records</td>
            <td className="border border-gray-300 px-4 py-2"><strong>Legal obligation</strong> (Art. 6 (1)(c))</td>
            <td className="border border-gray-300 px-4 py-2">Dutch tax law obliges us to keep business correspondence.</td>
          </tr>
        </tbody>
      </table>
    </div>
    <p className="mb-4">
      We do <strong>not</strong> engage in profiling or automated decision-making that produces legal or similarly significant effects (Art. 22 GDPR).
    </p>

    <h2 className="text-2xl font-semibold mt-6 mb-3">5. Retention Periods</h2>
    <ul className="list-disc pl-5 mb-4">
      <li className="mb-2">Technical log files and scan data: <strong>1 month</strong></li>
      <li className="mb-2">Google Analytics data: <strong>14 months</strong> (configured data‑retention period within GA4)</li>
      <li className="mb-2">E‑mails and other fiscally relevant documents: <strong>7 years</strong> (Dutch tax retention requirement)</li>
    </ul>

    <h2 className="text-2xl font-semibold mt-6 mb-3">6. Recipients and Processing Locations</h2>
    <ul className="list-disc pl-5 mb-4">
      <li className="mb-2">Hosting: <strong>Microsoft Azure</strong> — EU data‑centres only. Microsoft acts as processor under its standard Product Terms; no separate DPA is concluded.</li>
      <li className="mb-2">Analytics: <strong>Google Ireland Limited</strong>, Gordon House, Barrow Street, Dublin 4, Ireland – provider of Google Analytics 4. We have concluded the Google Ads Data Processing Terms, which incorporate the Standard Contractual Clauses. Aggregated analytics data are principally stored on servers located in the European Union; in exceptional cases Google LLC (United States) may access the data under the SCCs.</li>
      <li className="mb-2">No data are disclosed to other third parties except where we are legally obliged to do so (e.g. to supervisory authorities).</li>
    </ul>

    <h2 className="text-2xl font-semibold mt-6 mb-3">7. Security Measures</h2>
    <p className="mb-4">
      We apply appropriate technical and organisational measures, including:
    </p>
    <ul className="list-disc pl-5 mb-4">
      <li className="mb-2">Transport Layer Security (HTTPS) for all traffic</li>
      <li className="mb-2">Encryption‑at‑rest features offered by Azure</li>
      <li className="mb-2">Azure firewalls and access controls</li>
      <li className="mb-2">Operational logging for maintenance and incident detection</li>
      <li className="mb-2">IP‑anonymisation and data‑sharing restrictions within Google Analytics</li>
    </ul>
    <p className="mb-4">
      We periodically review these controls to ensure continued adequacy.
    </p>

    <h2 className="text-2xl font-semibold mt-6 mb-3">8. Cookies and Analytics</h2>
    <p className="mb-4">
      The site uses <strong>essential cookies</strong> required for basic site operation. Additionally, with your explicit consent, we use <strong>Google Analytics 4 (GA4)</strong> to collect anonymised information about how the website is used. You can control your preferences at any time via the cookie banner shown at the bottom of the page.
    </p>
    <p className="mb-4">
      <strong>Analytics cookies:</strong> These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site. All information is aggregated and therefore anonymised before storage. If you do not allow these cookies we will not include your visit in our statistics.
    </p>
    <p className="mb-4">
      <strong>Google Analytics:</strong> We have configured GA4 with the following safeguards:
    </p>
    <ul className="list-disc pl-5 mb-4">
      <li className="mb-2">IP‑anonymisation is enabled prior to any processing by Google.</li>
      <li className="mb-2">Data‑sharing options and advertising features are disabled.</li>
      <li className="mb-2">User‑level identifiers are retained for <strong>14 months</strong>, after which they are automatically deleted.</li>
      <li className="mb-2">EU data‑region is enforced for data collection wherever technically possible.</li>
    </ul>
    <p className="mb-4">
      Google processes the information on our behalf in accordance with its privacy policy and the Data Processing Terms referenced above. You can withdraw your consent at any time by revisiting the cookie banner or by enabling the official Google browser add‑on for opting out of Analytics.
    </p>

    <h2 className="text-2xl font-semibold mt-6 mb-3">9. Your Rights</h2>
    <p className="mb-4">
      Under the GDPR you have the right to: access, rectification, erasure, restriction, data portability and objection (where applicable). You may exercise these rights by e‑mailing <strong><a href="mailto:privacy@quantumsafeaudit.com" className="text-blue-600 hover:underline">privacy@quantumsafeaudit.com</a></strong>. We respond within <strong>four weeks</strong>. You also have the right to lodge a complaint with the Dutch Data Protection Authority (Autoriteit Persoonsgegevens, PO Box 93374, 2509 AJ The Hague, <a href="http://www.autoriteitpersoonsgegevens.nl" className="text-blue-600 hover:underline">www.autoriteitpersoonsgegevens.nl</a>).
    </p>

    <h2 className="text-2xl font-semibold mt-6 mb-3">10. Children</h2>
    <p className="mb-4">
      The Service is aimed at business users and is <strong>not intended for persons under 16 years of age</strong>. We do not knowingly process children's data.
    </p>

    <h2 className="text-2xl font-semibold mt-6 mb-3">11. Data Breach Procedure</h2>
    <p className="mb-4">
      Suspected personal‑data breaches are logged and investigated without undue delay. Where required, we will notify the Autoriteit Persoonsgegevens and affected individuals within <strong>72 hours</strong>.
    </p>

    <h2 className="text-2xl font-semibold mt-6 mb-3">12. Changes to This Policy</h2>
    <p className="mb-4">
      We may update this policy from time to time. The "Last updated" date at the top indicates the current version. Significant changes will be communicated by posting a prominent notice on the website.
    </p>

    <h2 className="text-2xl font-semibold mt-6 mb-3">13. Contact</h2>
    <p className="mb-4">
      For any privacy‑related questions or requests please write to:
      <br /><strong>E‑mail:</strong> <a href="mailto:privacy@quantumsafeaudit.com" className="text-blue-600 hover:underline">privacy@quantumsafeaudit.com</a>
      <br /><strong>Postal address:</strong> available on request from the Dutch Chamber of Commerce (KvK).
    </p>
  </div>
);

export default PrivacyPolicy;
